import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useEffect, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useForm } from "react-hook-form";
import useFocusErrorForm from "../../hooks/useFocusErrorForm";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import {
  startSendEmailOtpCode,
  startVerifyRegistrationOtpCode,
  startRegisterAdvertiserUser,
} from "../../actions/auth";
import {
  ALREADY_HAVE_AN_ACCOUNT,
  BUTTON_CONTINUE,
  BUTTON_REGISTER,
  BUTTON_SIGN_UP,
  BUTTON_RESEND_CODE,
  BUTTON_VERIFY_CODE,
  BUTTON_GO_BACK,
  ERROR_OCCURRED,
  LOG_IN,
  AUTH_PAGE_BRANDING_LOGO_ALT,
  AUTH_PAGE_DOCUMENT_TITLE_REGISTER,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import { LaunchError } from "../../utils/errors";
import {
  fetchSignInMethodsForEmail,
  GoogleAuthProvider,
  signInWithCredential,
} from "firebase/auth";
import { auth } from "../../lib/firebase";
import _ from "lodash";
import { waitDelay } from "../../utils/date";
import { BOX_SHADOW_FIELDS, INPUT_COLOR_BORDER } from "../../utils/colors";
import { BORDER_RADIUS, PLAN_TYPES } from "../../utils/constants";
import Button from "../../components/Buttons/Button";
import AuthLayout from "../../components/Auth/AuthLayout";
import AuthPageHelmet from "../../components/Auth/AuthPageHelmet";
import RegisterEmailStep from "./components/RegisterEmailStep";
import RegisterVerificationStep from "./components/RegisterVerificationStep";
import RegisterPasswordStep from "./components/RegisterPasswordStep";

const REGISTER_STEPS = {
  EMAIL: "email",
  OTP: "otp",
  PASSWORD: "password",
};

const getMaskedEmail = ({ email = "" }) => {
  const emailNormalized = String(email).trim();
  const [localPart = "", domainPart = ""] = emailNormalized.split("@");
  if (!localPart || !domainPart) return "*****";

  const maskedLocalPart = `${localPart.charAt(0)}${"*".repeat(
    Math.max(localPart.length - 1, 5)
  )}`;
  return `${maskedLocalPart}@${domainPart}`;
};

const Register = () => {
  const { control, handleSubmit, formState, setValue, watch, getValues } =
    useForm({
      defaultValues: {
        email: "",
        password: "",
        confirmPassword: "",
        otpCode: "",
        userID: "",
        promotionalCode: "",
        discoverySource: "",
      },
      mode: "onSubmit",
    });
  const errors = formState.errors;
  useFocusErrorForm(formState);
  const [currentStep, setCurrentStep] = useState(REGISTER_STEPS.EMAIL);
  const [modeForm, setModeForm] = useState("email");
  const [currentProviderData, setCurrentProviderData] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(30);
  const [isResendAllowed, setIsResendAllowed] = useState(false);
  const [isResendingCode, setIsResendingCode] = useState(false);

  const showEmailStep = currentStep === REGISTER_STEPS.EMAIL;
  const showOtpStep = currentStep === REGISTER_STEPS.OTP;
  const showPasswordStep = currentStep === REGISTER_STEPS.PASSWORD;

  const dispatch = useDispatchApp();
  const navigate = useNavigate();
  const { t, i18n } = useTranslationApp();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isTrial = searchParams.get("trial") === "true";
  const planType = searchParams.get("planType");
  const promoFromUrl =
    searchParams.get("promo")?.trim() || searchParams.get("ref")?.trim() || "";
  const emailMasked = getMaskedEmail({ email: watch("email") });
  const { theme } = useSelectorApp((state) => state.ui);

  const resendCode = async () => {
    if (!isResendAllowed) return;

    setIsResendingCode(true);
    await onContinueEmailStep();
    setIsResendingCode(false);
    setCountdown(30);
    setIsResendAllowed(false);
  };

  const onRegister = async (formValues) => {
    try {
      const registerData = {
        ...formValues,
        userIDProvider: currentProviderData?.userID,
      };

      if (isTrial) {
        registerData.trial = true;
      }

      if (planType && PLAN_TYPES.includes(planType)) {
        registerData.planType = planType;
      }

      const promotionalCodeTrimmed = String(formValues.promotionalCode ?? "")
        .trim()
        .toUpperCase();
      if (promotionalCodeTrimmed) {
        registerData.promotionalCode = promotionalCodeTrimmed;
      }

      const discoverySource = String(formValues.discoverySource ?? "").trim();
      if (discoverySource) {
        registerData.discoverySource = discoverySource;
      }

      const response = await dispatch(
        startRegisterAdvertiserUser(registerData)
      );
      if (!response.ok) {
        setError(response.message);
        return;
      }

      if (modeForm === "google") {
        await signInWithCredential(auth, currentProviderData.credential);
        await waitDelay(250);
      }

      navigate("/settings");

      return;
    } catch (err) {
      const errorFormatted = dispatch(
        LaunchError(err, false, [], {
          functionName: "onRegister",
        })
      );
      setError(errorFormatted.message);
    }
  };

  const onVerifyOtpCode = async () => {
    try {
      const { email, otpCode } = getValues();
      const responseVerifyOtp = await dispatch(
        startVerifyRegistrationOtpCode({
          email,
          otpCode,
        })
      );

      if (!responseVerifyOtp?.ok) {
        setError(responseVerifyOtp?.message || t(ERROR_OCCURRED));
        return false;
      }

      if (responseVerifyOtp?.otpCode) {
        setValue("otpCode", responseVerifyOtp.otpCode);
      }

      setError(null);
      setCurrentStep(REGISTER_STEPS.PASSWORD);
      return true;
    } catch (err) {
      const errorFormatted = dispatch(
        LaunchError(err, false, [], {
          functionName: "onVerifyOtpCode",
        })
      );
      setError(errorFormatted.message);
      return false;
    }
  };

  const onContinueEmailStep = async () => {
    try {
      const formValues = getValues();
      const { email } = formValues;
      const language = i18n?.language?.startsWith("es") ? "es" : "en";

      const result = await fetchSignInMethodsForEmail(auth, email);
      if (result.length > 0) {
        navigate("/login", {
          replace: true,
          state: {
            existingAccountLoginEmail: String(email).trim(),
            fromExistingAccountRegistration: true,
          },
        });
        return;
      }

      const responseSendOtp = await dispatch(
        startSendEmailOtpCode({
          email,
          lng: language,
        })
      );
      if (!responseSendOtp?.ok) {
        setError(responseSendOtp?.message || t(ERROR_OCCURRED));
        return;
      }

      setError(null);
      setCurrentStep(REGISTER_STEPS.OTP);
      setCountdown(30);
      setIsResendAllowed(false);
    } catch (err) {
      const errorFormatted = dispatch(
        LaunchError(err, false, [], {
          functionName: "onContinueEmailStep",
        })
      );
      setError(errorFormatted.message);
    }
  };

  const onSubmit = async (formValues) => {
    setError(null);

    setIsFetching(true);
    try {
      if (showEmailStep) {
        await onContinueEmailStep();
        return;
      }

      if (showOtpStep) {
        await onVerifyOtpCode();
        return;
      }

      await onRegister(formValues);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (promoFromUrl.trim()) {
      setValue("promotionalCode", promoFromUrl.trim().toUpperCase());
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const state = location?.state || {};
    if (_.isEmpty(state)) return;

    const providerData = state;
    if (providerData.modeForm === "google") {
      const credential = GoogleAuthProvider.credential(
        providerData.credential.idToken,
        providerData.credential.accessToken
      );
      setCurrentProviderData({
        ...providerData,
        credential,
      });
    }
    setValue("email", providerData.email);
    setValue("userID", providerData.userID);
    setModeForm(providerData.modeForm);

    navigate(location.pathname, { state: {} });

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    let timer;
    if (showOtpStep && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else if (showOtpStep && countdown === 0) {
      setIsResendAllowed(true);
    }
    return () => clearTimeout(timer);
  }, [countdown, showOtpStep]);

  useEffect(() => {
    dispatch(startRegisterAdvertiserUser({}, false));
    // eslint-disable-next-line
  }, []);

  return (
    <AuthLayout>
      <AuthPageHelmet
        documentTitleKey={AUTH_PAGE_DOCUMENT_TITLE_REGISTER}
        nameBusiness={theme.nameBusiness}
      />

      {showOtpStep && (
        <Box
          sx={{
            width: "100%",
            maxWidth: 400,
            mx: "auto",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <IconButton
            type="button"
            onClick={() => setCurrentStep(REGISTER_STEPS.EMAIL)}
            sx={{
              p: 0,
              mb: 2,
              color: "text.secondary",
              "&:hover": {
                backgroundColor: "action.hover",
              },
            }}
            aria-label={t(BUTTON_GO_BACK)}
          >
            <ArrowBackIcon />
          </IconButton>
        </Box>
      )}

      <Box
        sx={{
          width: "100%",
          maxWidth: 400,
          mx: "auto",
          textAlign: "start",
        }}
      >
        <Avatar
          src={theme.logoLogin}
          alt={t(AUTH_PAGE_BRANDING_LOGO_ALT)}
          variant="square"
          sx={{
            width: "100px",
            height: "auto",
            maxHeight: "60px",
            ".MuiAvatar-img": {
              maxWidth: "160px",
              objectFit: "contain",
            },
          }}
        />
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          width: "100%",
          maxWidth: 400,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <div id="sign-up-button" />

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              backgroundColor: "fields.background",
              color: "background.paperContrastText",
              border: `1px solid ${INPUT_COLOR_BORDER}`,
              borderRadius: BORDER_RADIUS,
              boxShadow: BOX_SHADOW_FIELDS,
            }}
          >
            {error}
          </Alert>
        )}

        {showEmailStep && (
          <RegisterEmailStep control={control} errors={errors} />
        )}
        {showOtpStep && (
          <RegisterVerificationStep
            control={control}
            errors={errors}
            emailMasked={emailMasked}
          />
        )}
        {showPasswordStep && (
          <RegisterPasswordStep
            control={control}
            errors={errors}
            watch={watch}
          />
        )}

        <Box sx={{ width: "100%", mt: 1 }}>
          {!showOtpStep && !showPasswordStep && (
            <>
              <Button
                loading={isFetching}
                type="submit"
                color="primary"
                fullWidth
                variant="contained"
              >
                {t(BUTTON_SIGN_UP)}
              </Button>

              {!currentProviderData && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: 1,
                    mt: 2,
                  }}
                >
                  <Typography variant="body2" align="left">
                    {t(ALREADY_HAVE_AN_ACCOUNT)}
                  </Typography>
                  <Link
                    to="/login"
                    style={{
                      textDecoration: "none",
                      fontSize: "14px",
                      fontWeight: 700,
                    }}
                  >
                    {t(LOG_IN)}
                  </Link>
                </Box>
              )}
            </>
          )}

          {showOtpStep && (
            <Box
              sx={{
                display: "flex",
                gap: 1,
              }}
            >
              <Button
                loading={isResendingCode}
                onClick={resendCode}
                disabled={!isResendAllowed || isFetching}
                color="secondary"
                fullWidth
                variant="contained"
              >
                {`${t(BUTTON_RESEND_CODE)}${
                  isResendAllowed ? "" : ` (${countdown})`
                }`}
              </Button>
              <Button
                loading={isFetching}
                type="submit"
                color="primary"
                fullWidth
                variant="contained"
              >
                {t(BUTTON_VERIFY_CODE)}
              </Button>
            </Box>
          )}

          {showPasswordStep && (
            <>
              <Button
                loading={isFetching}
                type="submit"
                color="primary"
                fullWidth
                variant="contained"
              >
                {t(!currentProviderData ? BUTTON_REGISTER : BUTTON_CONTINUE)}
              </Button>

              {!currentProviderData && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: 1,
                    mt: 2,
                  }}
                >
                  <Typography variant="body2" align="left">
                    {t(ALREADY_HAVE_AN_ACCOUNT)}
                  </Typography>
                  <Link
                    to="/login"
                    style={{
                      textDecoration: "none",
                      fontSize: "14px",
                      fontWeight: 700,
                    }}
                  >
                    {t(LOG_IN)}
                  </Link>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </AuthLayout>
  );
};

export default Register;
